<template>
  <div class="relative" ref="menuRef" @keydown.esc="open = false">
    <!-- Jeśli nie zalogowany -->
    <button v-if="!auth.isAuthenticated" @click="goToLogin" class="user-icon-btn">
      <img src="@/assets/images/user.svg" :alt="t('userMenu.login')" class="user-icon-img" />
    </button>
    <!-- Jeśli zalogowany -->
    <div v-else>
      <button @click="toggleMenu" class="user-icon-btn relative">
        <img v-if="profileImageUrl" :src="profileImageUrl" :alt="t('userMenu.user')" class="user-avatar-img" />
        <img v-else src="@/assets/images/user.svg" :alt="t('userMenu.user')" class="user-icon-img" />
        <!-- Unread messages badge -->
        <div 
          v-if="messagesStore.hasUnreadMessages" 
          class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
        >
          {{ messagesStore.unreadCount > 99 ? '99+' : messagesStore.unreadCount }}
        </div>
      </button>
      <transition name="fade">
        <div v-if="open" class="user-menu-dropdown">
          <div class="flex flex-col items-center p-6 pb-2">
            <div class="user-menu-avatar">
              <img v-if="profileImageUrl" :src="profileImageUrl" :alt="t('userMenu.user')" class="user-avatar-img-lg" />
              <img v-else src="@/assets/images/user.svg" :alt="t('userMenu.user')" class="user-icon-img-lg" />
            </div>
            <div class="text-accent text-xl mt-2 mb-4 uppercase tracking-wide text-center">{{ user?.firstName || user?.email || t('userMenu.user') }}</div>
          </div>
          <div class="flex flex-col gap-3 px-6 pb-6">
            <router-link to="/profile" class="user-menu-link" @click="closeMenu">{{ t('userMenu.profile') }}</router-link>
            <router-link to="/my-offers" class="user-menu-link" @click="closeMenu">{{ t('userMenu.myOffers') }}</router-link>
            <router-link to="/profile/messages" class="user-menu-link" @click="closeMenu">
              <div class="flex items-center justify-center">
                <span>{{ t('messages.title') }}</span>
                <div 
                  v-if="messagesStore.hasUnreadMessages" 
                  class="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold ml-2 flex-shrink-0"
                >
                  {{ messagesStore.unreadCount > 99 ? '99+' : messagesStore.unreadCount }}
                </div>
              </div>
            </router-link>
            <router-link to="/profile/settings" class="user-menu-link" @click="closeMenu">{{ t('userMenu.settings') }}</router-link>
            <button @click="logout" class="user-menu-logout">{{ t('userMenu.logout') }}</button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMessagesStore } from '@/stores/messages'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const open = ref(false)
const auth = useAuthStore()
const messagesStore = useMessagesStore()
const user = computed(() => auth.user)
const router = useRouter()
const menuRef = ref(null)

const profileImageUrl = computed(() => {
  if (user.value && user.value.profileImage && typeof user.value.profileImage === 'object') {
    const id = user.value.profileImage.id
    return `${import.meta.env.VITE_API_URL}/media/${id}/file?version=thumbnail`
  }
  return null
})

// Watch for authentication changes
watch(() => auth.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    await messagesStore.fetchUnreadCount()
  } else {
    messagesStore.reset()
  }
})

async function toggleMenu() {
  if (!open.value && auth.isAuthenticated) {
    // Sprawdź nowe wiadomości przy otwieraniu menu
    await messagesStore.fetchUnreadCount()
  }
  open.value = !open.value
}
function goToLogin() {
  router.push('/login')
}
function logout() {
  auth.logout()
  open.value = false
  router.push('/login')
}
function handleClickOutside(event) {
  if (menuRef.value && !menuRef.value.contains(event.target)) {
    open.value = false
  }
}
function closeMenu() {
  open.value = false
}
onMounted(async () => {
  window.addEventListener('click', handleClickOutside)
  if (auth.isAuthenticated && !auth.user) {
    await auth.fetchUserData()
  }
  
  // Initialize messages store if authenticated
  if (auth.isAuthenticated) {
    await messagesStore.fetchUnreadCount()
  }
})
onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
})
</script> 